import {Button, IconButton} from '@wandb/components';
import React from 'react';
import {useParams} from 'react-router-dom';

import {RunOverflowMenu} from '../../../pages/Run/RunOverflowMenu';
import {setRunDetailsSourcePath} from '../../../pages/Run/setRunDetailsSourcePath';
import {usePartMaybe} from '../../../state/views/hooks';
import {runColor} from '../../../util/colors';
import {useStatsigGateRunDetailsTabbed} from '../../../util/rampFeatureFlags';
import {getCustomRunName} from '../../../util/runs';
import * as urls from '../../../util/urls';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {useSemanticLegendSettingsContext} from '../../WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {useSingleRunBackUrl} from './useSingleRunBackUrl';

export const SingleRunHeader = () => {
  const {run} = useSingleRunModeContext();
  const isRunDetailsTabbedEnabled = useStatsigGateRunDetailsTabbed();
  const backUrl = useSingleRunBackUrl();
  const {customRunColorsRef, customRunNamesRef} = useWorkspaceRefsContext();
  const customRunNames = usePartMaybe(customRunNamesRef);
  const customRunColors = usePartMaybe(customRunColorsRef);
  const {semanticLegendSettings, semanticLegendRunColorOptions} =
    useSemanticLegendSettingsContext();
  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();

  if (!run) {
    return null;
  }

  const runDisplayColor = runColor({
    run,
    customRunColors,
    semanticLegendSettings: {
      ...semanticLegendSettings,
      runColorOptions: semanticLegendRunColorOptions,
    },
    colorPaletteOption,
  });
  const customRunName = getCustomRunName(run.name, false, customRunNames);

  return (
    <div className={'p-16 border-b border-b-moon-250 md:pb-0 md:border-b-0'}>
      <header className="flex gap-10 md:gap-32 justify-between">
        <div className="flex flex-1 min-w-0 items-center gap-12">
          <IconButton
            icon="back"
            aria-label="Exit single-run mode"
            href={backUrl}
          />
          <div
            className="h-16 w-16 rounded-full"
            style={{backgroundColor: runDisplayColor}} // cannot use tailwind for dynamic values
          />
          <h2 className="mt-2 w-0 flex-1 truncate md:overflow-visible md:whitespace-normal md:text-clip md:break-all text-lg font-semibold">
            {customRunName ? (
              <>
                <span>{customRunName} </span>
                <span className="text-sm text-moon-500">
                  ({run.displayName})
                </span>
              </>
            ) : (
              run.displayName
            )}
          </h2>
        </div>
        {!isRunDetailsTabbedEnabled && (
          <div className="flex gap-8">
            <Button
              variant="secondary"
              size="md"
              startIcon="list-bullets"
              href={urls.run({
                entityName,
                projectName,
                name: run.name,
                tab: 'overview',
              })}
              onClick={setRunDetailsSourcePath}>
              Run details
            </Button>
            <div className="hidden md:block">
              <Button
                variant="secondary"
                size="md"
                startIcon="code-alt"
                href={urls.run({
                  entityName,
                  projectName,
                  name: run.name,
                  tab: 'logs',
                })}>
                Logs
              </Button>
            </div>
          </div>
        )}
        {isRunDetailsTabbedEnabled && (
          <RunOverflowMenu
            entityName={entityName}
            projectName={projectName}
            runName={run.name}
          />
        )}
      </header>
    </div>
  );
};
